import type { ToolCallGroupItem } from "src/base-components/agents/ToolCallGroup";
import {
  type AIMessage,
  type HumanMessage,
  type SessionMessagesResponseMessagesInner,
  type ToolCallContentBlock,
  type ToolMessage,
  ToolType,
} from "src/clients/agent-management";
import { mentionBlockToReferenceMarkdown } from "src/unifiedCopilot/chat/contentBlocks";
import { toolCallActionVerb } from "src/unifiedCopilot/chat/toolActionVerbs";

/** tool_call_id → the tool message that answered it. */
export const buildToolResultMap = (
  messages: SessionMessagesResponseMessagesInner[],
): Map<string, ToolMessage> =>
  new Map(
    messages
      .filter((m): m is { type: "tool" } & ToolMessage => m.type === "tool")
      .map((m) => [m.tool_call_id, m] as const),
  );

type VisibleChatMessage =
  | ({ type: "human" } & HumanMessage)
  | ({ type: "ai" } & AIMessage);

// Tool messages are folded into the AI message that called them.
const visibleChatMessages = (
  messages: SessionMessagesResponseMessagesInner[],
): VisibleChatMessage[] =>
  messages.filter(
    (message): message is VisibleChatMessage =>
      message.type === "ai" ||
      (message.type === "human" && Boolean(message.content_blocks?.length)),
  );

export type ChatTurn = {
  key: string;
  messages: VisibleChatMessage[];
};

/** A turn opens on each human message and absorbs the AI messages after it. */
export const chatTurns = (
  messages: SessionMessagesResponseMessagesInner[],
): ChatTurn[] =>
  visibleChatMessages(messages).reduce<ChatTurn[]>((turns, message, idx) => {
    const openTurn = turns.at(-1);
    if (message.type === "human" || openTurn === undefined) {
      turns.push({
        key: message.id ?? `${message.type}-${idx}`,
        messages: [message],
      });
    } else {
      openTurn.messages.push(message);
    }
    return turns;
  }, []);

// Interim shape: the AI-turn flattening below is a placeholder until the
// upcoming trace-view rework replaces it.
export type TurnItem =
  | { kind: "text"; key: string; text: string }
  | {
      kind: "tool-thread";
      key: string;
      items: ToolCallGroupItem[];
      /** Model iterations behind `items`; see `aiStepCount`. */
      stepCount: number;
    };

type AIContentBlock = NonNullable<AIMessage["content_blocks"]>[number];

type InlineBlock = Extract<AIContentBlock, { type: "text" | "mention" }>;

const isInlineBlock = (block: AIContentBlock): block is InlineBlock =>
  block.type === "text" || block.type === "mention";

const inlineBlockMarkdown = (block: InlineBlock): string =>
  block.type === "text" ? block.text : mentionBlockToReferenceMarkdown(block);

/**
 * Consecutive text and mention blocks merge into one markdown string
 * (mentions serialize to their inline reference-tag form) so pills render
 * mid-sentence; a tool call ends the current string and stands alone. Blocks
 * of unknown kinds degrade to nothing instead of crashing.
 */
const appendAiItem = (
  toolResults: Map<string, ToolMessage>,
  items: ToolCallGroupItem[],
  block: AIContentBlock,
  key: string,
): ToolCallGroupItem[] => {
  if (isInlineBlock(block)) {
    const previous = items.at(-1);
    if (previous?.kind === "text") {
      return [
        ...items.slice(0, -1),
        { ...previous, text: `${previous.text}${inlineBlockMarkdown(block)}` },
      ];
    }
    return [...items, { kind: "text", key, text: inlineBlockMarkdown(block) }];
  }
  if (block.type === "tool_call") {
    // The structured-output tool carries the final answer, not a tool call
    // the user should see.
    if (block.metadata.type === ToolType.STRUCTURED_OUTPUT) {
      return items;
    }
    return [
      ...items,
      {
        kind: "tool",
        toolCall: block,
        toolResult: toolResults.get(block.id) ?? null,
      },
    ];
  }
  return items;
};

/**
 * Flattens a turn's AI messages into one ordered list of text runs and tool
 * calls (with results). Runs never merge across messages.
 */
export const aiFlatItems = (
  turn: ChatTurn,
  toolResults: Map<string, ToolMessage>,
): ToolCallGroupItem[] =>
  turn.messages
    .filter((m): m is { type: "ai" } & AIMessage => m.type === "ai")
    .flatMap((message, msgIdx) => {
      const messageKey = message.id ?? `${turn.key}-ai-${msgIdx}`;
      return (message.content_blocks ?? []).reduce<ToolCallGroupItem[]>(
        (items, block, blockIdx) =>
          appendAiItem(
            toolResults,
            items,
            block,
            `${messageKey}-text-${blockIdx}`,
          ),
        [],
      );
    });

/**
 * How many model iterations of the turn called a tool. The calls one AI
 * message makes run in parallel, so five calls over two iterations are two
 * steps rather than five.
 */
const aiStepCount = (turn: ChatTurn): number =>
  turn.messages
    .filter((m): m is { type: "ai" } & AIMessage => m.type === "ai")
    .filter((message) =>
      (message.content_blocks ?? []).some(
        (block) =>
          block.type === "tool_call" &&
          block.metadata.type !== ToolType.STRUCTURED_OUTPUT,
      ),
    ).length;

/**
 * Splits a turn into what the user sees: tool calls, plus any text the agent
 * wrote between its first and last tool call, collapse into one thread, while
 * the opening text and the final answer stay outside. While the turn is still
 * running, trailing text stays in the thread too, since it may be narration
 * for a tool call that has not arrived yet.
 */
export const aiTurnItems = (
  turn: ChatTurn,
  toolResults: Map<string, ToolMessage>,
  isInProgress: boolean,
): TurnItem[] => {
  const flat = aiFlatItems(turn, toolResults);
  const firstToolIdx = flat.findIndex((item) => item.kind === "tool");
  const foldEndIdx =
    isInProgress && firstToolIdx !== -1
      ? flat.length
      : flat.findLastIndex((item) => item.kind !== "text");
  const isInThread = (item: ToolCallGroupItem, idx: number) =>
    item.kind === "tool" || (idx > firstToolIdx && idx < foldEndIdx);
  const thread = flat.filter(isInThread);

  return flat.flatMap((item, idx): TurnItem[] => {
    if (idx === firstToolIdx) {
      return [
        {
          kind: "tool-thread",
          key: `${turn.key}-thread`,
          items: thread,
          stepCount: aiStepCount(turn),
        },
      ];
    }
    return item.kind === "text" && !isInThread(item, idx) ? [item] : [];
  });
};

/** What the running turn is doing right now, derived from its messages. */
export type TurnProgress = {
  /**
   * Active-tense phrase for the newest open tool call the copy names; null
   * when it names none of them.
   */
  actionVerb: string | null;
  /**
   * The agent's latest text in the turn on one line. Null before it has
   * written any, and while its only text is the turn's opening one.
   */
  narration: string | null;
};

const isAiMessage = (
  message: ChatTurn["messages"][number],
): message is { type: "ai" } & AIMessage => message.type === "ai";

const aiContentBlocks = (turn: ChatTurn): AIContentBlock[] =>
  turn.messages
    .filter(isAiMessage)
    .flatMap((message) => message.content_blocks ?? []);

const inlineBlockPlainText = (block: InlineBlock): string =>
  block.type === "text" ? block.text : block.label;

/**
 * The last run of consecutive text and mention blocks, as one line: mentions
 * read by their label, and line breaks and runs of spaces collapse so the
 * result fits a status line.
 *
 * Only a run the trace folds into the tool thread counts. The text before the
 * turn's first call renders inline under the message, so previewing it here
 * would read the same paragraph out twice.
 */
const latestNarration = (
  blocks: AIContentBlock[],
  firstCallIdx: number,
): string | null => {
  const lastInlineIdx = blocks.findLastIndex(isInlineBlock);
  if (lastInlineIdx === -1 || lastInlineIdx < firstCallIdx) {
    return null;
  }
  const runStartIdx =
    blocks.slice(0, lastInlineIdx).findLastIndex((b) => !isInlineBlock(b)) + 1;
  const text = blocks
    .slice(runStartIdx, lastInlineIdx + 1)
    .filter(isInlineBlock)
    .map(inlineBlockPlainText)
    .join("")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 0 ? text : null;
};

/**
 * The structured-output call carries the final answer rather than work in
 * progress, so the trace counts it as neither a step nor an open call.
 */
const isVisibleToolCall = (
  block: AIContentBlock,
): block is { type: "tool_call" } & ToolCallContentBlock =>
  block.type === "tool_call" &&
  block.metadata.type !== ToolType.STRUCTURED_OUTPUT;

/**
 * Pairs the verb of the newest open tool call with the agent's latest
 * narration. Calls issued in parallel are all open at once, so the verb comes
 * from the newest call the copy names rather than the newest call outright.
 * Null while no tool call is waiting on its result.
 */
export const turnProgress = (
  turn: ChatTurn,
  toolResults: Map<string, ToolMessage>,
): TurnProgress | null => {
  const blocks = aiContentBlocks(turn);
  const openCalls = blocks.filter(
    (block): block is { type: "tool_call" } & ToolCallContentBlock =>
      isVisibleToolCall(block) && !toolResults.has(block.id),
  );
  if (openCalls.length === 0) {
    return null;
  }
  return {
    actionVerb:
      openCalls.map(toolCallActionVerb).findLast((verb) => verb !== null) ??
      null,
    narration: latestNarration(blocks, blocks.findIndex(isVisibleToolCall)),
  };
};
